import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { MapPin, Globe, Calendar, Instagram, Facebook, ArrowLeft, HandCoins, Send, AlertCircle, Loader2, Users, TrendingUp, Coffee, Megaphone, ArrowRight, Target, ZoomIn } from "lucide-react";
import WhatsappIcon from "../components/ui/WhatsappIcon";
import axios from "axios";
import { PUBLIC_API } from "../lib/api";
import { chiffresCarte } from "../lib/etatCampagne";
import TeroumaLogo from "../components/ui/TeroumaLogo";
import CampaignImageLightbox from "../components/shared/CampaignImageLightbox";

// New prayer-section-based schedule display
const SCHEDULE_SECTION_LABELS = {
  semaine: "Semaine (Lun–Ven)",
  vendredi_soir: "Vendredi soir",
  chabbat_matin: "Chabbat matin",
  chabbat_aprem: "Chabbat après-midi",
  dimanche: "Dimanche",
};
const SERVICE_LABELS = {
  shaharit: "Chaharit", minha: "Minha", arvit: "Arvit",
  kabalat_chabbat: "Kabalat Chabbat",
  seouda_chlichit: "Séouda Chlichit", arvit_havdala: "Arvit / Havdala",
};

// Generate WhatsApp link from phone number
function generateWhatsAppLink(phone) {
  if (!phone) return null;
  // Remove all non-digits
  const digits = phone.replace(/\D/g, "");
  if (digits.length < 10) return null;

  // If starts with 0 (France), convert to +33
  if (digits.startsWith("0")) {
    return `https://wa.me/33${digits.slice(1)}`;
  }
  return `https://wa.me/${digits}`;
}

// Build structured schedule from the flat {section_service: time} dict
function buildScheduleSections(schedule) {
  const sections = {};
  for (const [rawKey, val] of Object.entries(schedule || {})) {
    if (!val) continue;
    // Try to match {section}_{service}
    const sectionKeys = Object.keys(SCHEDULE_SECTION_LABELS);
    const matchedSection = sectionKeys.find(s => rawKey.startsWith(`${s}_`));
    if (matchedSection) {
      const svc = rawKey.slice(matchedSection.length + 1);
      if (!sections[matchedSection]) sections[matchedSection] = [];
      sections[matchedSection].push({ svc, val, label: SERVICE_LABELS[svc] || svc });
    } else {
      // Legacy flat key — show as-is
      if (!sections.__legacy) sections.__legacy = [];
      sections.__legacy.push({ svc: rawKey, val, label: rawKey.replace(/_/g, " ") });
    }
  }
  return sections;
}

function TagChip({ label }) {
  return (
    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium"
      style={{ background: "rgba(13,27,51,0.07)", color: "#0D1B33" }}>
      {label}
    </span>
  );
}

export default function OrganizationPublicPage() {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [campaigns, setCampaigns] = useState([]);

  useEffect(() => {
    const controller = new AbortController();
    axios.get(`${PUBLIC_API}/organizations/public/${orgId}/profile`, { signal: controller.signal })
      .then(r => setOrg(r.data))
      .catch(e => { if (e.name !== "CanceledError") setError(true); })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [orgId]);

  // Fetch campaigns scoped to this organization (status="active" only)
  useEffect(() => {
    if (!orgId) return;
    axios.get(`${PUBLIC_API}/campaigns/public`, { params: { organization_id: orgId, limit: 24 } })
      .then(r => setCampaigns(r.data?.campaigns || []))
      .catch(() => setCampaigns([]));
  }, [orgId]);

  return (
    <div className="min-h-screen" style={{ background: "#F4F3EF" }}>
      {/* Navbar */}
      <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur border-b border-stone-200">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/"><TeroumaLogo textColor="#0D1B33" /></Link>
          <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-900 transition-colors">
            <ArrowLeft className="h-4 w-4" /> Retour
          </button>
        </div>
      </nav>

      {loading && (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-7 w-7 animate-spin text-stone-300" />
        </div>
      )}

      {error && (
        <div className="flex items-center justify-center py-24 px-4">
          <div className="text-center max-w-sm">
            <div className="h-14 w-14 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="h-6 w-6 text-red-400" />
            </div>
            <p className="text-stone-700 font-medium mb-1">Organisation introuvable</p>
            <p className="text-sm text-stone-400 mb-5">Cette fiche n'est plus disponible.</p>
            <Link to="/" className="text-sm underline text-stone-400">Retour à l'accueil</Link>
          </div>
        </div>
      )}

      {org && (
        <div className="max-w-3xl mx-auto px-4 py-10 space-y-6">

          {/* Hero card */}
          <div className="bg-white rounded-3xl shadow-sm border border-stone-100 overflow-hidden">
            {/* Photo banner or gradient band */}
            {org.org_photo_base64 ? (
              <div className="h-64 sm:h-72 w-full overflow-hidden bg-stone-100">
                <img
                  src={`data:image/jpeg;base64,${org.org_photo_base64}`}
                  alt={org.name}
                  className="w-full h-full object-cover object-center"
                  style={{ objectPosition: "center 55%" }}
                />
              </div>
            ) : (
              <div className="h-2" style={{ background: "linear-gradient(90deg, #0D1B33, #B8962E)" }} />
            )}
            <div className="p-8">
              <div className="flex items-start gap-4 mb-4">
                <div className="h-16 w-16 rounded-2xl flex items-center justify-center text-xl font-bold text-white shrink-0"
                  style={{ background: "linear-gradient(135deg, #0D1B33 0%, #1B2A4A 100%)" }}>
                  {org.name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <h1 className="text-2xl font-semibold text-stone-900 leading-tight">{org.name}</h1>
                  {org.city && (
                    <div className="flex items-center gap-1 mt-1">
                      <MapPin className="h-4 w-4 text-stone-400" />
                      <span className="text-stone-500 text-sm">{org.city}</span>
                    </div>
                  )}
                  {org.address && !org.city && (
                    <p className="text-xs text-stone-400 mt-0.5">{org.address}</p>
                  )}
                </div>
              </div>

              {/* Stats (when enabled by org) */}
              {org.show_donor_stats && (org.donor_count != null || org.total_collected != null) && (
                <div className="flex gap-4 mb-4">
                  {org.donor_count != null && (
                    <div className="flex items-center gap-2 text-sm text-stone-600">
                      <Users className="h-4 w-4" style={{ color: "#B8962E" }} />
                      <span><strong className="text-stone-900">{org.donor_count.toLocaleString("fr-FR")}</strong> donateurs</span>
                    </div>
                  )}
                  {org.total_collected != null && org.total_collected > 0 && (
                    <div className="flex items-center gap-2 text-sm text-stone-600">
                      <TrendingUp className="h-4 w-4" style={{ color: "#B8962E" }} />
                      <span><strong className="text-stone-900">{org.total_collected.toLocaleString("fr-FR", { minimumFractionDigits: 0 })} €</strong> collectés</span>
                    </div>
                  )}
                </div>
              )}

              {/* Tags */}
              {org.tags && org.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {org.tags.map(t => <TagChip key={t} label={t} />)}
                </div>
              )}

              {/* Welcome message */}
              {org.welcome_message && (
                <p className="text-stone-700 italic text-sm leading-relaxed border-l-2 pl-4 mb-4"
                  style={{ borderColor: "#B8962E" }}>
                  "{org.welcome_message}"
                </p>
              )}

              {/* Description */}
              {org.public_description && (
                <p className="text-stone-600 text-sm leading-relaxed mb-4">{org.public_description}</p>
              )}

              {/* Quick infos */}
              <div className="flex flex-wrap gap-4 text-sm text-stone-500 mb-6">
                {org.rabbi_name && (
                  <span><span className="font-medium text-stone-700">Rabbin :</span> {org.rabbi_name}</span>
                )}
                {org.founding_year && (
                  <span><span className="font-medium text-stone-700">Fondée en :</span> {org.founding_year}</span>
                )}
                {org.website && (
                  <a href={org.website.startsWith("http") ? org.website : `https://${org.website}`}
                    target="_blank" rel="noreferrer"
                    className="flex items-center gap-1 hover:text-stone-900 transition-colors">
                    <Globe className="h-3.5 w-3.5" />
                    {org.website.replace(/^https?:\/\//, "")}
                  </a>
                )}
              </div>

              {/* CTAs */}
              <div className="flex flex-col gap-2.5">
                {/* Primary CTA */}
                <Link to={`/donate/${org.id}`}
                  className="flex items-center justify-center gap-2 py-3.5 rounded-2xl text-sm font-semibold text-white transition-colors"
                  style={{ background: "#0D1B33" }}
                  onMouseEnter={e => e.currentTarget.style.background = "#1B2A4A"}
                  onMouseLeave={e => e.currentTarget.style.background = "#0D1B33"}>
                  <HandCoins className="h-4 w-4" /> Faire un don
                </Link>
                {/* Secondary CTAs */}
                <div className="flex gap-2.5">
                  {!(org?.disabled_modules || []).includes("kiddoush") && (
                  <Link to={`/kiddoush?org=${org.id}`}
                    className="flex-1 flex items-center justify-center gap-2 py-3 rounded-2xl text-sm font-semibold border-2 transition-all"
                    style={{ borderColor: "#B8962E", color: "#B8962E" }}
                    onMouseEnter={e => { e.currentTarget.style.background = "rgba(184,150,46,0.08)"; }}
                    onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}>
                    <Coffee className="h-4 w-4" /> Offrir un Kiddoush
                  </Link>
                  )}
                  <Link to={`/organisations/${org.id}/contact`}
                    className="flex-1 flex items-center justify-center gap-2 py-3 rounded-2xl text-sm font-semibold border-2 border-stone-200 text-stone-600 transition-all hover:border-stone-400 hover:bg-stone-50">
                    <Send className="h-4 w-4" /> Contacter
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* ─── Campagnes de l'organisation ─────────────────────────── */}
          {campaigns.length > 0 && !(org?.disabled_modules || []).includes("campaigns") && (
            <div className="bg-white rounded-3xl shadow-sm border border-stone-100 p-6">
              <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <Megaphone className="h-4 w-4" style={{ color: "#B8962E" }} />
                  <h2 className="font-semibold text-stone-900">
                    Campagnes en cours
                    <span className="ml-2 text-xs font-normal text-stone-400">
                      ({campaigns.length})
                    </span>
                  </h2>
                </div>
                <Link
                  to="/campagnes"
                  className="text-xs text-stone-500 hover:text-stone-900 inline-flex items-center gap-1 transition-colors"
                >
                  Toutes les campagnes <ArrowRight className="h-3 w-3" />
                </Link>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {campaigns.slice(0, 6).map(c => (
                  <OrgCampaignCard key={c.id} campaign={c} />
                ))}
              </div>

              {campaigns.length > 6 && (
                <div className="text-center mt-4">
                  <Link
                    to="/campagnes"
                    className="text-xs inline-flex items-center gap-1 px-4 py-2 rounded-full border border-stone-200 text-stone-600 hover:border-stone-400 hover:bg-stone-50 transition-all"
                  >
                    Voir les {campaigns.length} campagnes
                    <ArrowRight className="h-3 w-3" />
                  </Link>
                </div>
              )}
            </div>
          )}

          {/* Horaires */}
          {org.schedule && Object.keys(org.schedule).some(k => org.schedule[k]) && (() => {
            const sections = buildScheduleSections(org.schedule);
            const orderedKeys = [...Object.keys(SCHEDULE_SECTION_LABELS), "__legacy"];
            return (
              <div className="bg-white rounded-3xl shadow-sm border border-stone-100 p-6">
                <div className="flex items-center gap-2 mb-5">
                  <Calendar className="h-4 w-4" style={{ color: "#B8962E" }} />
                  <h2 className="font-semibold text-stone-900">Horaires des offices</h2>
                </div>
                <div className="space-y-4">
                  {orderedKeys.filter(k => sections[k]?.length > 0).map(sectionKey => (
                    <div key={sectionKey}>
                      <p className="text-[10px] uppercase tracking-[0.15em] text-stone-400 font-semibold mb-1.5">
                        {sectionKey === "__legacy" ? "Autres" : SCHEDULE_SECTION_LABELS[sectionKey]}
                      </p>
                      <div className="space-y-1">
                        {sections[sectionKey].map(({ svc, val, label }) => (
                          <div key={svc} className="flex items-center justify-between py-1.5 border-b border-stone-50 last:border-0">
                            <span className="text-sm text-stone-600">{label}</span>
                            <span className="text-sm font-medium text-stone-800 font-mono">{val}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* Liens sociaux */}
          {org.social_links && Object.values(org.social_links).some(v => v) && (
            <div className="bg-white rounded-3xl shadow-sm border border-stone-100 p-6">
              <h2 className="font-semibold text-stone-900 mb-4">Nous retrouver</h2>
              <div className="flex flex-wrap gap-3">
                {org.social_links?.whatsapp && generateWhatsAppLink(org.social_links.whatsapp) && (
                  <a href={generateWhatsAppLink(org.social_links.whatsapp)}
                    target="_blank" rel="noreferrer"
                    className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium border border-stone-200 hover:bg-stone-50 transition-colors text-stone-700">
                    <WhatsappIcon className="h-4 w-4 text-green-500" /> WhatsApp
                  </a>
                )}
                {org.social_links.instagram && (
                  <a href={`https://instagram.com/${org.social_links.instagram.replace("@", "")}`}
                    target="_blank" rel="noreferrer"
                    className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium border border-stone-200 hover:bg-stone-50 transition-colors text-stone-700">
                    <Instagram className="h-4 w-4 text-pink-500" /> Instagram
                  </a>
                )}
                {org.social_links.facebook && (
                  <a href={org.social_links.facebook.startsWith("http") ? org.social_links.facebook : `https://facebook.com/${org.social_links.facebook}`}
                    target="_blank" rel="noreferrer"
                    className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium border border-stone-200 hover:bg-stone-50 transition-colors text-stone-700">
                    <Facebook className="h-4 w-4 text-blue-600" /> Facebook
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Footer CTA */}
          <div className="text-center py-4">
            <Link to="/" className="text-sm text-stone-400 hover:text-stone-700 transition-colors">
              ← Voir toutes les organisations
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Campaign card (compact, used on the organization profile page) ──────
const BACKEND_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

function OrgCampaignCard({ campaign }) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const theme = campaign.theme_color || "#0D1B33";
  // Mêmes chiffres que la page campagne et la vignette WhatsApp : le PROMIS,
  // pourcentage du serveur arrondi vers le bas ; les paniers en unités.
  const carte = chiffresCarte(campaign);
  const pct = carte.objectif ? carte.pourcentage : null;
  // Prefer image_data_uri (embedded base64) — bypasses the flaky /image.png
  // endpoint that was producing broken-image icons on Vercel.
  const imgSrc = campaign.image_data_uri
    ? campaign.image_data_uri
    : (campaign.image_url
        ? (campaign.image_url.startsWith("http") ? campaign.image_url : `${BACKEND_BASE}${campaign.image_url}`)
        : null);
  const endLabel = campaign.end_date
    ? new Date(campaign.end_date).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" })
    : null;

  return (
    <>
    <Link
      to={`/c/${campaign.id}`}
      className="group block bg-white border border-stone-200 rounded-2xl overflow-hidden hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 no-underline"
    >
      {imgSrc ? (
        <div className="h-24 overflow-hidden relative">
          <img src={imgSrc} alt={campaign.title}
            className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setLightboxOpen(true);
            }}
            className="absolute top-1.5 right-1.5 z-10 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-semibold text-white bg-black/55 hover:bg-black/75 backdrop-blur-md transition-colors"
            aria-label="Voir l'image en grand"
            title="Voir l'image en grand"
          >
            <ZoomIn className="h-2.5 w-2.5" />
            Agrandir
          </button>
        </div>
      ) : (
        <div className="h-1.5" style={{ background: `linear-gradient(90deg, ${theme}, #B8962E)` }} />
      )}

      <div className="p-4">
        <h3 className="font-semibold text-stone-900 text-sm leading-snug line-clamp-2 mb-2">
          {campaign.title}
        </h3>

        {pct !== null && (
          <div className="mb-2">
            <div className="flex justify-between text-[11px] mb-1">
              <span className="font-semibold text-stone-700 tabular-nums">
                {carte.unites
                  ? `${carte.valeur} / ${carte.objectif} ${campaign.unit_label || "unité"}${carte.objectif > 1 ? "s" : ""}`
                  : new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(carte.valeur)}
              </span>
              <span className="text-stone-400 tabular-nums">{pct}%</span>
            </div>
            <div className="h-1 bg-stone-100 rounded-full overflow-hidden">
              <div className="h-full rounded-full transition-all duration-700"
                style={{ width: `${carte.largeur}%`, background: `linear-gradient(90deg, ${theme}, #B8962E)` }} />
            </div>
          </div>
        )}

        <div className="flex items-center justify-between pt-2 text-[10px] text-stone-500">
          {endLabel ? (
            <span className="inline-flex items-center gap-1">
              <Calendar className="h-3 w-3" /> {endLabel}
            </span>
          ) : <span />}
          <span className="font-medium" style={{ color: theme }}>
            Participer <ArrowRight className="h-3 w-3 inline" />
          </span>
        </div>
      </div>
    </Link>
    <CampaignImageLightbox
      src={imgSrc}
      alt={campaign.title}
      open={lightboxOpen}
      onClose={() => setLightboxOpen(false)}
    />
    </>
  );
}
